import { useEffect } from 'react';

type DensityMode = 'compact' | 'comfortable' | 'spacious';

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const computeDensity = () => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const viewport = window.visualViewport;
  const visualScale = viewport?.scale ?? 1;
  const dpr = window.devicePixelRatio || 1;
  const effectiveWidth = viewportWidth * dpr * visualScale;
  const effectiveHeight = viewportHeight * dpr * visualScale;

  let mode: DensityMode = 'comfortable';
  if (effectiveWidth >= 2600 || effectiveHeight >= 1500 || viewportWidth >= 1500) {
    mode = 'compact';
  } else if (viewportWidth <= 1180 || viewportHeight <= 760) {
    mode = 'spacious';
  }

  const fontSize =
    mode === 'compact'
      ? clamp(17 + ((viewportWidth - 1440) / 900) * 1.15, 16.8, 18.8)
      : mode === 'spacious'
        ? clamp(16 + ((1180 - viewportWidth) / 900) * 0.4, 15.8, 16.6)
      : 16;

  const shellMax =
    mode === 'compact'
      ? clamp(viewportWidth - 12, 1620, 2440)
      : mode === 'spacious'
        ? clamp(viewportWidth - 28, 320, 1480)
        : clamp(viewportWidth - 40, 920, 1720);

  const contentScale = mode === 'compact' ? 1.1 : mode === 'spacious' ? 1.03 : 1;
  const panelPadding = mode === 'compact' ? 1.12 : mode === 'spacious' ? 1.04 : 0.92;
  const navWidth = mode === 'compact' ? 320 : mode === 'spacious' ? 284 : 260;
  const sectionMinHeight = clamp(viewportHeight - (mode === 'compact' ? 148 : 250), 320, 1180);

  return {
    mode,
    fontSize,
    shellMax,
    contentScale,
    panelPadding,
    navWidth,
    sectionMinHeight
  };
};

export function useAdaptiveDensity() {
  useEffect(() => {
    const root = document.documentElement;

    const applyDensity = () => {
      const density = computeDensity();
      root.dataset.uiDensity = density.mode;
      root.style.setProperty('--font-size', `${density.fontSize}px`);
      root.style.setProperty('--aql-shell-max-width', `${density.shellMax}px`);
      root.style.setProperty('--aql-content-scale', `${density.contentScale}`);
      root.style.setProperty('--aql-panel-padding-factor', `${density.panelPadding}`);
      root.style.setProperty('--aql-nav-width', `${density.navWidth}px`);
      root.style.setProperty('--aql-section-min-height', `${density.sectionMinHeight}px`);
    };

    applyDensity();
    window.addEventListener('resize', applyDensity, { passive: true });
    window.visualViewport?.addEventListener('resize', applyDensity, { passive: true });

    return () => {
      window.removeEventListener('resize', applyDensity);
      window.visualViewport?.removeEventListener('resize', applyDensity);
    };
  }, []);
}
